import React from 'react';
import { Link } from 'react-router-dom';

function AirportPickUp() {
  return (
    <article className="seo-page-container" style={{ 
      padding: 'clamp(15px, 4vw, 30px)', 
      background: '#fff', 
      margin: 'clamp(10px, 3vw, 20px)', 
      borderRadius: '4px', 
      textAlign: 'left', 
      fontFamily: 'Arial, sans-serif',
      boxSizing: 'border-box'
    }}>
      
      {/* HEADER HERO SECTION */}
      <header>
        <h1 style={{ 
          color: '#0b3c5d', 
          marginBottom: '15px', 
          fontSize: 'clamp(1.5rem, 6vw, 2.4rem)', 
          fontWeight: '700', 
          lineHeight: '1.3' 
        }}>
          Medical Tourism Airport Pick-up & Patient Logistics: The Complete Arrival Guide
        </h1>
        <p style={{ color: '#444', lineHeight: '1.8', fontSize: 'clamp(0.95rem, 3.5vw, 1.1rem)', marginBottom: '25px' }}>
          Traveling overseas for critical healthcare or major surgery requires meticulous planning that goes far beyond selecting a surgical facility. For international patients, the moments immediately following arrival at a foreign airport are crucial. 
          This comprehensive guide covers how medical travelers handle <strong>airport pick-up arrangements, non-emergency medical transportation, airport ground logistics</strong>, and seamless transit to their accommodation or diagnostic center.
        </p>
      </header>

      {/* CALL TO ACTION BUTTON */}
      <div style={{ marginBottom: '35px' }}>
        <Link to="/submit-query" style={{ 
          background: '#28a745', 
          color: '#fff', 
          padding: '14px 24px', 
          textDecoration: 'none', 
          borderRadius: '4px', 
          fontWeight: 'bold', 
          display: 'inline-block', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontSize: '0.95rem',
          textAlign: 'center',
          boxSizing: 'border-box',
          maxWidth: '100%'
        }}>
          Download Medical Travel Logistics Checklist (Free) →
        </Link>
      </div>

      {/* SECTION 1: THE CRITICAL REALITY OF PATIENT TRANSIT */}
      <section style={{ marginBottom: '35px' }}>
        <h2 style={{ color: '#0b3c5d', borderBottom: '2px solid #f4f8fb', paddingBottom: '8px', fontSize: 'clamp(1.2rem, 5vw, 1.5rem)' }}>
          Why Dedicated Patient Airport Transfers are Vital
        </h2>
        <p style={{ color: '#555', lineHeight: '1.7', marginBottom: '15px', fontSize: '0.95rem' }}>
          Unlike standard vacationers, medical tourists often arrive experiencing physical discomfort, mobility limitations, or pre-operative anxiety. Navigating public transportation systems, hailing unregulated local cabs, or managing heavy luggage while dealing with language barriers can compromise patient safety and raise stress levels before admission.
        </p>
        <p style={{ color: '#555', lineHeight: '1.7', margin: 0, fontSize: '0.95rem' }}>
          Standard global industry recommendations for a secure arrival include pre-booking designated ground transport operators who specialize in healthcare travel protocols. Specialized transport vehicles ensure clean interiors, proper climate control, adequate space for orthopedic or cardiac patients, and professional drivers who understand local clinical destinations.
        </p>
      </section>

      {/* SECTION 2: EXPECTED LOGISTICAL STEPS */}
      <section style={{ marginBottom: '35px' }}>
        <h2 style={{ color: '#0b3c5d', borderBottom: '2px solid #f4f8fb', paddingBottom: '8px', fontSize: 'clamp(1.2rem, 5vw, 1.5rem)' }}>
          Step-by-Step Ground Protocol for International Patients
        </h2>
        <p style={{ color: '#555', lineHeight: '1.6', fontSize: '0.95rem' }}>
          When reviewing your medical travel logistics, standard administrative timelines generally follow this sequence:
        </p>

        <div style={{ background: '#f4f8fb', padding: 'clamp(15px, 4vw, 20px)', margin: '25px 0', borderRadius: '4px', borderLeft: '4px solid #005ba1' }}>
          <h3 style={{ marginTop: '0', color: '#005ba1', fontSize: '1.2rem', marginBottom: '10px' }}>📋 Standard Arrival Sequence Checklist:</h3>
          <ul style={{ marginTop: '10px', listStyleType: 'square', paddingLeft: '20px', lineHeight: '1.8', color: '#333', fontSize: '0.95rem' }}>
            <li><strong>Pre-Flight Dispatch:</strong> Share your confirmed flight numbers, passport names, and arrival timetables with your chosen transport service provider or hotel concierge at least 72 hours prior to take-off.</li>
            <li><strong>The Meeting Point:</strong> Look for professional airport desk personnel or designated greeting zones inside the international arrival terminal holding customized name boards.</li>
            <li><strong>Luggage & Mobility Assistance:</strong> Ensure your transport booking includes dedicated physical help to transition through customs and terminal exits safely.</li>
            <li><strong>Direct-to-Destination Transit:</strong> Proceed via a pre-arranged route straight to your specialized hotel lodging or directly to the clinical admissions desk for primary diagnostic blood panels.</li>
          </ul>
        </div>
      </section>

      {/* SECTION 3: SAFETY & MOBILITY EVALUATION - ADAPTIVE GRID FOR PHONES */}
      <section style={{ marginBottom: '35px' }}>
        <h2 style={{ color: '#0b3c5d', borderBottom: '2px solid #f4f8fb', paddingBottom: '8px', fontSize: 'clamp(1.2rem, 5vw, 1.5rem)' }}>
          Verifying Vehicle Requirements for Medical Transits
        </h2>
        <p style={{ color: '#555', lineHeight: '1.6', fontSize: '0.95rem' }}>
          Because Medical Trip World operates strictly as an open informational venue and independent directory, we strongly urge traveling patients to self-verify transit details directly with their elected logistics providers or transport agencies before arrival. Ensure your ride matches your medical needs:
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px', marginTop: '15px' }}>
          <div style={{ padding: '15px', background: '#fafafa', borderRadius: '4px', border: '1px solid #eee' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#0b3c5d', fontSize: '1.05rem' }}>Orthopedic & Spine Comfort</h4>
            <p style={{ margin: '0', fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
              Patients arriving for total knee, hip, or spine surgeries should request spacious multi-purpose vehicles (MPVs) with elevated seating profiles and generous legroom to avoid sharp, painful joint bending.
            </p>
          </div>
          <div style={{ padding: '15px', background: '#fafafa', borderRadius: '4px', border: '1px solid #eee' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#0b3c5d', fontSize: '1.05rem' }}>Post-Op Discharge Wheelchair Fleet</h4>
            <p style={{ margin: '0', fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
              When traveling back from the clinical facility to the airport post-surgery, verify that the service vehicle has an accessible trunk ramp or trunk space capacity capable of securely housing collapsible medical wheelchairs and walkers.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4: IN-DEPTH FAQ */}
      <section style={{ marginBottom: '35px', background: '#fff', border: '1px solid #e0e0e0', padding: 'clamp(15px, 4vw, 20px)', borderRadius: '4px' }}>
        <h3 style={{ color: '#0b3c5d', marginTop: '0', marginBottom: '20px', fontSize: '1.25rem' }}>
          Frequently Asked Questions (FAQs) – Patient Airport Logistics
        </h3>
        
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 'bold', color: '#333', margin: '0 0 5px 0', fontSize: '0.95rem' }}>Does a standard medical visa allow airport pickup arrangements?</p>
          <p style={{ color: '#666', margin: '0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            Yes. Holding a valid national Medical Visa or Medical Attendant Visa grants you legal clearance to clear border custom lines and exit terminals directly to any pre-booked private transport, hotel shuttle, or hospital ambulance service waiting outside.
          </p>
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 'bold', color: '#333', margin: '0 0 5px 0', fontSize: '0.95rem' }}>What happens if an international flight gets delayed or rescheduled?</p>
          <p style={{ color: '#666', margin: '0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            Most premium international patient transfer agencies monitor flight telemetry numbers in real time. However, to guarantee your driver is at the terminal gate when you land, patients are advised to immediately text flight delay notices to their transport point-of-contact.
          </p>
        </div>
        
        <div style={{ marginBottom: '5px' }}>
          <p style={{ fontWeight: 'bold', color: '#333', margin: '0 0 5px 0', fontSize: '0.95rem' }}>Does Medical Trip World operate its own fleet of airport vehicles?</p>
          <p style={{ color: '#666', margin: '0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            No. Medical Trip World is an independent informational platform and directory. We do not own transport vehicles, manage logistics networks, or hold official partnerships with local transit providers. All arrangements are researched, confirmed, and paid for directly by the patient with their chosen hospital, hotel, or private transport agency.
          </p>
        </div>
      </section>

      {/* RE-ENGAGEMENT FOOTER */}
      <footer style={{ borderTop: '1px solid #eee', paddingTop: '20px', textAlign: 'center' }}>
        <p style={{ fontSize: '1.05rem', fontWeight: 'bold', color: '#333', margin: '0 0 15px 0' }}>Begin Mapping Out Your Medical Travel Journey Safely</p>
        <div style={{ display: 'flex', gap: '12px', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
          <style>{`
            @media (max-width: 550px) {
              .footer-btn { width: 100% !important; text-align: center; box-sizing: border-box; }
            }
          `}</style>
          <Link to="/submit-query" className="footer-btn" style={{ background: '#28a745', color: '#fff', padding: '12px 20px', textDecoration: 'none', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.9rem', display: 'inline-block' }}>
            Submit General Information Request
          </Link>
          <Link to="/compare-costs" className="footer-btn" style={{ background: '#fff', color: '#0b3c5d', border: '2px solid #0b3c5d', padding: '10px 20px', textDecoration: 'none', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.9rem', display: 'inline-block' }}>
            View Estimated Cost Charts
          </Link>
        </div>
      </footer>

    </article>
  );
}

export default AirportPickUp;